import { useCallback, useContext, useEffect, useState } from 'react';
import { SessionContext } from '@/auth/SessionContext';
import { ORDERBUMP_GROUPING_EXPERIMENTAL_FLAG } from '@/consts/flags';

// Por usuário: no mesmo navegador entram contas diferentes (equipe, multiconta), e a escolha
// de uma não vale para a outra.
const storageKey = (userId) => `cakto:my-sales:group-bumps:${userId ?? 'anon'}`;

// Só o "desligado" fica gravado: o padrão é agrupar, então nada salvo é ligado.
function readGrouped(key) {
  try {
    return window.localStorage.getItem(key) !== 'off';
  } catch {
    return true;
  }
}

function writeGrouped(key, grouped) {
  try {
    if (grouped) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, 'off');
  } catch {
    // Sem storage (aba anônima, bloqueio): a escolha vale até recarregar a página.
  }
}

/**
 * Minhas Vendas com os order bumps dentro da venda principal (CKTPL-1363).
 *
 * Lê o `SessionContext` direto, e não o `useSessionContext`, porque o `OrdersProvider` também
 * roda fora da sessão (testes, telas sem login): sem usuário, a função fica desligada.
 */
export default function useOrderBumpGrouping() {
  const session = useContext(SessionContext);
  const user = session?.user;
  const available = Boolean(
    user?.experimental_features?.includes(ORDERBUMP_GROUPING_EXPERIMENTAL_FLAG)
  );
  const key = storageKey(user?.id);
  const [preference, setPreference] = useState(() => readGrouped(key));

  // O usuário pode chegar depois do primeiro render: relê a escolha da conta certa.
  useEffect(() => {
    setPreference(readGrouped(key));
  }, [key]);

  const setGrouped = useCallback(
    (grouped) => {
      setPreference(grouped);
      writeGrouped(key, grouped);
    },
    [key]
  );

  return { available, grouped: available && preference, setGrouped };
}
